import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Dropdown } from "../../ui/dropdown/Dropdown";
import { DropdownItem } from "../../ui/dropdown/DropdownItem";
import { DayOfWeek } from "../../../types/restaurant";
import { useRestaurants } from "../../../hooks/useRestaurants";
import { useUser } from "../../../context/UserInfoProvider";
import { LoadingSpinner } from "../../ui/LoadingSpinner";
import { PageLoadingView } from "../../common/PageLoadingView";
import Input from "../../../components/form/input/InputField";
import RestaurantStatus from "./subcomponents/RestuarantComponents/RestaurantStatus";
import ImageUploader from "../../form/ImageUploader";

// Define allDays outside the component to ensure a stable reference
const allDays: DayOfWeek[] = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// Utility functions for time handling
const formatTimeForDisplay = (timeString: string | null): string => {
  if (!timeString) return "No Data Available";
  
  // Handle HH:MM:SS format - extract hours and minutes
  // No timezone conversion - treat times as local time strings
  const parts = timeString.split(":");
  const hours = parts[0] || "00";
  const minutes = parts[1] || "00";
  
  const hour24 = parseInt(hours, 10);
  const minute = parseInt(minutes, 10);

  // Convert 24-hour to 12-hour format without timezone conversion
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const ampm = hour24 >= 12 ? "PM" : "AM";

  return `${hour12}:${minute.toString().padStart(2, "0")} ${ampm}`;
};

const formatTimeForInput = (timeString: string | null): string => {
  if (!timeString) return "";

  // Handle HH:MM:SS format - extract just HH:MM for HTML time input
  // HTML time inputs use 24-hour format (HH:MM) and don't need seconds
  const parts = timeString.split(":");
  const hours = parts[0] || "00";
  const minutes = parts[1] || "00";
  
  // Return in HH:MM format (24-hour, no timezone conversion)
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const validateTimeSequence = (open: string, close: string): boolean => {
  // Convert all times to minutes since midnight for easier comparison
  const toMinutes = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const openMinutes = toMinutes(open);
  const closeMinutes = toMinutes(close);

  // Basic check: open time must be before close time
  return openMinutes < closeMinutes;
};

const RestaurantsData: React.FC = () => {
  const { hotelId: userInfoHotelId } = useUser();
  const hotelId = userInfoHotelId;

  // Use the updated hook instead of local state management
  const {
    items: restaurants,
    isLoading: isLoadingRestaurants,
    error: hookError,
    selectedRestaurantId,
    setSelectedRestaurant: setSelectedRestaurantId,
    getSelectedRestaurant,
    operatingSchedules,
    isLoadingSchedules,
    fetchOperatingSchedules,
    updateRestaurantDescription,
    updateItem,
    scheduleIntervals,
    upsertRestaurantOperatingSchedule,
    deleteRestaurantOperatingSchedule,
  } = useRestaurants({ hotelId: hotelId || "" });

  // Helper function to normalize time strings for comparison
  const normalizeTime = (time: string) => {
    if (!time) return "";
    // Ensure HH:MM format with leading zeros
    const parts = time.split(":");
    if (parts.length >= 2) {
      return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
    }
    return time;
  };

  // UI-specific state (keep in component)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [tempDescription, setTempDescription] = useState("");
  const [isEditingOperatingHours, setIsEditingOperatingHours] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [operatingHoursError, setOperatingHoursError] = useState<string | null>(null);

  // New state to track if all data for the selected restaurant is ready for display
  const [isRestaurantDataReady, setIsRestaurantDataReady] = useState(false);

  const [tempOperatingHours, setTempOperatingHours] = useState<
    Record<
      string,
      {
        open: string; // Corresponds to openTime
        close: string; // Corresponds to closeTime
      }
    >
  >({});

  // Get selected restaurant from hook
  const selectedRestaurant = getSelectedRestaurant();

  // Helper functions
  const selectRestaurantById = useCallback(
    (restaurantId: string) => {
      setSelectedRestaurantId(restaurantId);
    },
    [setSelectedRestaurantId]
  );

  const stableEmptyOperatingScheduleMap: Record<
    string,
    { openTime: string | null; closeTime: string | null }
  > = useMemo(() => Object.freeze({}), []);

  // Transform the array of schedule entries into the map structure expected by the component
  const currentOperatingSchedulesMap: Record<
    string,
    { openTime: string | null; closeTime: string | null }
  > = useMemo(() => {
    if (!operatingSchedules || operatingSchedules.length === 0) {
      return stableEmptyOperatingScheduleMap;
    }
    const newMap: Record<
      string,
      { openTime: string | null; closeTime: string | null }
    > = {};
    allDays.forEach((day) => {
      // Find an active operating schedule for the current day
      // Prioritize 'OperatingHours' type if available, otherwise take first active one.
      // Find all active schedules for this day with OperatingHours type
      const activeSchedulesForDay = operatingSchedules.filter(
        (s) =>
          s.dayofweek === day &&
          s.isactive &&
          s.scheduletype === "OperatingHours"
      );
      
      // If no OperatingHours schedules, fall back to any active schedule for this day
      const schedulesToUse = activeSchedulesForDay.length > 0 
        ? activeSchedulesForDay 
        : operatingSchedules.filter((s) => s.dayofweek === day && s.isactive);
      
      // If multiple schedules exist, prefer the one with the most recent updatedat
      // or the one with the scheduleId that matches the restaurant's primary schedule
      let activeScheduleForDay = schedulesToUse.length > 0 
        ? schedulesToUse.reduce((latest, current) => {
            const currentUpdated = (current as any).updatedat || (current as any).updatedAt || "";
            const latestUpdated = (latest as any).updatedat || (latest as any).updatedAt || "";
            // Prefer the most recently updated, or if no timestamp, use the first
            return currentUpdated > latestUpdated ? current : latest;
          })
        : null;

      if (activeScheduleForDay) {
        // Backend returns starttime and endtime at the top level of the schedule entry
        // (from the joined scheduleinterval table)
        // Handle both snake_case (from API) and camelCase (from transformed data)
        const startTime = 
          (activeScheduleForDay as any).starttime || 
          (activeScheduleForDay as any).startTime ||
          (activeScheduleForDay.interval as any)?.starttime ||
          (activeScheduleForDay.interval as any)?.startTime ||
          null;
        const endTime = 
          (activeScheduleForDay as any).endtime || 
          (activeScheduleForDay as any).endTime ||
          (activeScheduleForDay.interval as any)?.endtime ||
          (activeScheduleForDay.interval as any)?.endTime ||
          null;

        newMap[day] = {
          openTime: startTime,
          closeTime: endTime,
        };
      } else {
        newMap[day] = { openTime: null, closeTime: null }; // Default to closed if no active schedule
      }
    });
    return newMap;
  }, [operatingSchedules, scheduleIntervals, stableEmptyOperatingScheduleMap]); // allDays is stable as it's outside component

  // Determine a valid UUID for uploads, checking several possible properties
  // Ref to track for which restaurant operating hours have been initialized
  const initialOperatingHoursSetForRestaurantId = useRef<string | null>(null);

  // Effect to set/reset isRestaurantDataReady
  useEffect(() => {
    if (selectedRestaurantId) {
      // When selectedRestaurantId changes, or related data is still loading, assume not ready.
      // The goal is to set ready only when selectedRestaurant AND its operatingSchedules are loaded.
      if (selectedRestaurant && operatingSchedules !== null) {
        // Potentially add checks for other derived states if they cause flashes
        setIsRestaurantDataReady(true);
      } else {
        setIsRestaurantDataReady(false); // Explicitly set to false if conditions not met
      }
    } else {
      setIsRestaurantDataReady(false); // No restaurant selected, so not ready.
    }
  }, [
    selectedRestaurantId,
    selectedRestaurant,
    operatingSchedules,
    setIsRestaurantDataReady,
  ]);

  useEffect(() => {
    if (selectedRestaurant) {
      setTempDescription(selectedRestaurant.description);
    }
  }, [selectedRestaurant]);

  // Effect to initialize tempOperatingHours for editing
  useEffect(() => {
    if (isEditingOperatingHours && selectedRestaurant) {
      // Only set/reset tempOperatingHours if:
      // 1. We just entered editing mode for a new restaurant (selectedRestaurant.restaurantId changed)
      // OR
      // 2. We were not editing for this restaurant before (initial ref ID doesn't match)
      // This prevents overwriting user's temp edits if currentOperatingSchedulesMap (derived from hook)
      // refreshes in the background for the same restaurant.
      if (
        initialOperatingHoursSetForRestaurantId.current !==
        selectedRestaurant.restaurantId
      ) {
        const newTempHoursData: typeof tempOperatingHours = {};
        allDays.forEach((day) => {
          const scheduleDay = currentOperatingSchedulesMap[day];
          const openTimeRaw = scheduleDay?.openTime;
          const closeTimeRaw = scheduleDay?.closeTime;
          
          // Debug logging
          if (openTimeRaw || closeTimeRaw) {
            console.log(`Initializing ${day} temp hours:`, {
              openTimeRaw,
              closeTimeRaw,
              openFormatted: openTimeRaw ? formatTimeForInput(openTimeRaw) : "",
              closeFormatted: closeTimeRaw ? formatTimeForInput(closeTimeRaw) : "",
            });
          }
          
          newTempHoursData[day] = {
            open: openTimeRaw ? formatTimeForInput(openTimeRaw) : "",
            close: closeTimeRaw ? formatTimeForInput(closeTimeRaw) : "",
          };
        });
        setTempOperatingHours(newTempHoursData);
        initialOperatingHoursSetForRestaurantId.current =
          selectedRestaurant.restaurantId;
      }
    }
  }, [
    isEditingOperatingHours,
    selectedRestaurant,
    currentOperatingSchedulesMap,
    tempOperatingHours,
  ]);

  // Initialize first restaurant selection
  useEffect(() => {
    if (
      Array.isArray(restaurants) &&
      restaurants.length > 0 &&
      !selectedRestaurantId
    ) {
      const firstRestaurantId =
        restaurants[0].restaurantId ||
        restaurants[0].id ||
        restaurants[0].restaurantid;
      if (firstRestaurantId) {
        selectRestaurantById(firstRestaurantId);
      }
    }
  }, [restaurants, selectedRestaurantId, selectRestaurantById]);

  const today = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", { weekday: "long" }) as DayOfWeek,
    []
  );

  const todayOperatingScheduleRaw = useMemo(
    () =>
      currentOperatingSchedulesMap
        ? currentOperatingSchedulesMap[today]
        : undefined,
    [currentOperatingSchedulesMap, today] // Depends on the transformed map
  );

  const restaurantStatusDisplayProps = useMemo(() => {
    // Always return schedule props, even if null/undefined
    // This allows RestaurantStatus to properly handle "No Data Available" state
    return {
      openTime: todayOperatingScheduleRaw?.openTime || null,
      closeTime: todayOperatingScheduleRaw?.closeTime || null,
    };
  }, [todayOperatingScheduleRaw]);

  const handleRestaurantChange = (restaurantId: string) => {
    if (selectedRestaurantId !== restaurantId) {
      // Check if it's an actual change
      selectRestaurantById(restaurantId);
      setIsDropdownOpen(false);
      setUpdateError(null);
      setOperatingHoursError(null);
      setIsEditingOperatingHours(false);
      setIsRestaurantDataReady(false); // Explicitly set to false on change
    }
  };

  const handleDescriptionSave = () => {
    if (!selectedRestaurant) {
      return;
    }

    if (selectedRestaurant.description === tempDescription) {
      setIsEditingDescription(false);
      return;
    }

    // Try different possible ID field names from the API
    const restaurantId =
      selectedRestaurant.restaurantId ||
      selectedRestaurant.id ||
      selectedRestaurant.restaurantid ||
      selectedRestaurantId;

    if (!restaurantId) {
      console.error(
        "Could not determine restaurant ID from:",
        selectedRestaurant
      );
      setUpdateError("Unable to identify restaurant for update");
      return;
    }

    setIsSaving(true);
    setUpdateError(null);

    updateRestaurantDescription(restaurantId, tempDescription)
      .then(() => {
        setIsEditingDescription(false);
      })
      .catch((err) => {
        setUpdateError(
          err instanceof Error ? err.message : "Failed to update description"
        );
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleDescriptionCancel = () => {
    if (selectedRestaurant) {
      setTempDescription(selectedRestaurant.description);
      setIsEditingDescription(false);
    }
  };

  const handleOperatingHoursChange = (
    day: string,
    field: "open" | "close",
    value: string
  ) => {
    setTempOperatingHours((prev) => {
      const newHoursForDay = {
        ...(prev[day] || {}),
        [field]: value,
      };
      const newHours = {
        ...prev,
        [day]: newHoursForDay,
      };

      if (newHoursForDay.open && newHoursForDay.close) {
        const openTime = newHoursForDay.open;
        const closeTime = newHoursForDay.close;
        setUpdateError(null);
        if (!validateTimeSequence(openTime, closeTime)) {
          setUpdateError("Open time must be before close time.");
        }
      } else {
        setUpdateError(null);
      }
      return newHours;
    });
  };

  const handleOperatingHoursSave = async () => {
    if (!selectedRestaurantId || !selectedRestaurant) {
      return;
    }

    setIsSaving(true);
    setUpdateError(null);
    setOperatingHoursError(null);
    let hasErrors = false;

    const promises = allDays.map(async (day) => {
      const tempDayData = tempOperatingHours[day];
      const newOpenInput = (tempDayData?.open || "").trim();
      const newCloseInput = (tempDayData?.close || "").trim();

      const originalSchedule = currentOperatingSchedulesMap[day];
      const originalOpenFormatted = formatTimeForInput(
        originalSchedule?.openTime
      ).trim();
      const originalCloseFormatted = formatTimeForInput(
        originalSchedule?.closeTime
      ).trim();

      const normalizedNewOpen = normalizeTime(newOpenInput);
      const normalizedNewClose = normalizeTime(newCloseInput);
      const normalizedOriginalOpen = normalizeTime(originalOpenFormatted);
      const normalizedOriginalClose = normalizeTime(originalCloseFormatted);

      const hasChanged =
        normalizedNewOpen !== normalizedOriginalOpen ||
        normalizedNewClose !== normalizedOriginalClose;

      if (!hasChanged) {
        // Skip if nothing changed - no API call needed
        return;
      }

      const existingActiveOperatingHoursEntryForDay = operatingSchedules?.find(
        (s) =>
          s.dayofweek === day &&
          s.restaurantid === selectedRestaurantId &&
          s.isactive &&
          s.scheduletype === "OperatingHours"
      );

      if (newOpenInput && newCloseInput) {
        if (!validateTimeSequence(newOpenInput, newCloseInput)) {
          hasErrors = true;
          setOperatingHoursError(
            (prev) =>
              (prev ? prev + "; " : "") +
              `Error on ${day}: Open time must be before close time.`
          );
          return;
        }

        try {
          // Additional check: if we're updating an existing entry and the interval hasn't changed,
          // we can skip the update to avoid conflict errors
          if (existingActiveOperatingHoursEntryForDay) {
            // Backend returns starttime and endtime at top level of schedule entry
            // Handle both snake_case (from API) and camelCase (from transformed data)
            const existingStartTime = 
              (existingActiveOperatingHoursEntryForDay as any).starttime ||
              (existingActiveOperatingHoursEntryForDay as any).startTime ||
              (existingActiveOperatingHoursEntryForDay.interval as any)?.starttime ||
              (existingActiveOperatingHoursEntryForDay.interval as any)?.startTime;
            const existingEndTime = 
              (existingActiveOperatingHoursEntryForDay as any).endtime ||
              (existingActiveOperatingHoursEntryForDay as any).endTime ||
              (existingActiveOperatingHoursEntryForDay.interval as any)?.endtime ||
              (existingActiveOperatingHoursEntryForDay.interval as any)?.endTime;
            
            if (existingStartTime && existingEndTime) {
              const existingOpenFormatted = formatTimeForInput(existingStartTime).trim();
              const existingCloseFormatted = formatTimeForInput(existingEndTime).trim();
              
              const normalizedExistingOpen = normalizeTime(existingOpenFormatted);
              const normalizedExistingClose = normalizeTime(existingCloseFormatted);
              
              // If times match exactly, skip the update
              if (
                normalizedNewOpen === normalizedExistingOpen &&
                normalizedNewClose === normalizedExistingClose
              ) {
                return; // No change needed, skip API call
              }
            }
          }
          
          // Debug logging to verify what we're sending
          console.log(`Saving ${day} - Input values:`, { 
            newOpenInput, 
            newCloseInput,
            originalOpen: originalSchedule?.openTime,
            originalClose: originalSchedule?.closeTime,
          });
          
          await upsertRestaurantOperatingSchedule(
            selectedRestaurantId,
            day,
            newOpenInput,
            newCloseInput,
            true,
            "OperatingHours",
            null,
            existingActiveOperatingHoursEntryForDay
          );
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : "Unknown error";
          const errorResponse = (err as any)?.response?.data?.error;
          const errorType = errorResponse?.type;
          const backendErrorMessage = errorResponse?.message || errorMessage;
          
          // Check if this is a conflict error (no actual changes made)
          const isConflictError = 
            backendErrorMessage.includes("conflicts with existing data") ||
            backendErrorMessage.includes("conflict") ||
            errorType === "ConflictError";
          
          if (isConflictError) {
            // For conflict errors, don't treat as an error - just show info message
            // Don't set hasErrors, so buttons remain enabled
            setOperatingHoursError(
              (prev) => (prev ? prev + "; " : "") + 
                `No changes were made to ${day}. The operating hours you entered are the same as the current values.`
            );
          } else {
            // For real errors, disable buttons
            hasErrors = true;
            setOperatingHoursError(
              (prev) => (prev ? prev + "; " : "") + 
                `Failed to save ${day}: ${backendErrorMessage}`
            );
          }
        }
      } else if (
        (newOpenInput && !newCloseInput) ||
        (!newOpenInput && newCloseInput)
      ) {
        hasErrors = true;
        setOperatingHoursError(
          (prev) =>
            (prev ? prev + "; " : "") +
            `Error on ${day}: Both open and close must be provided.`
        );
      } else if (!newOpenInput && !newCloseInput) {
        if (
          existingActiveOperatingHoursEntryForDay &&
          existingActiveOperatingHoursEntryForDay.restaurantopscheduleid
        ) {
          try {
            await deleteRestaurantOperatingSchedule(
              selectedRestaurantId,
              existingActiveOperatingHoursEntryForDay.restaurantopscheduleid
            );
          } catch (err) {
            hasErrors = true;
            setOperatingHoursError(
              (prev) =>
                (prev ? prev + "; " : "") +
                `Failed to mark ${day} as closed: ${
                  err instanceof Error ? err.message : "Unknown error"
                }`
            );
          }
        }
      }
    });

    await Promise.all(promises);

    if (!hasErrors) {
      await fetchOperatingSchedules();
      setOperatingHoursError(null);
      setIsEditingOperatingHours(false);
    }
    setIsSaving(false);
  };

  const handleHeaderPhotoUpload = useCallback(
    async (key: string) => {
      if (!selectedRestaurant) return;
      try {
        setIsSaving(true);
        // Use the hook's updateItem function instead of direct API call
        await updateItem(selectedRestaurant.restaurantId, { headerPhoto: key });
      } catch (err) {
        console.error("Error updating header photo", err);
        setUpdateError("Failed to update header photo");
      } finally {
        setIsSaving(false);
      }
    },
    [selectedRestaurant, updateItem]
  );

  const handleHeaderPhotoDelete = useCallback(async () => {
    if (!selectedRestaurant) return;
    try {
      setIsSaving(true);
      // Use the hook's updateItem function instead of direct API call
      await updateItem(selectedRestaurant.restaurantId, { headerPhoto: "" });
    } catch (err) {
      console.error("Error deleting header photo", err);
      setUpdateError("Failed to delete header photo");
    } finally {
      setIsSaving(false);
    }
  }, [selectedRestaurant, updateItem]);

  // New combined loading check for initial display, now including isRestaurantDataReady
  const shouldShowLoading = isLoadingRestaurants ||
    (isLoadingSchedules && !selectedRestaurant) ||
    (!isRestaurantDataReady && selectedRestaurantId); // Show spinner if a restaurant is selected but its data isn't fully ready

  if (shouldShowLoading) {
    return <PageLoadingView />;
  }

  if (!restaurants || restaurants.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500 dark:text-gray-400">No restaurants found</p>
      </div>
    );
  }

  if (!selectedRestaurant) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500 dark:text-gray-400">
          Please select a restaurant
        </p>
      </div>
    );
  }

  return (
    <div className="w-full p-6 font-sans text-gray-800 dark:text-white">
      {updateError && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded">
          {updateError}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">{selectedRestaurant.name}</h1>
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
            >
              <ChevronDownIcon className="w-5 h-5 text-gray-800 dark:text-gray-400" />
            </button>
            <Dropdown
              isOpen={isDropdownOpen}
              onClose={() => setIsDropdownOpen(false)}
              className="min-w-[200px]"
            >
              {restaurants.map((restaurant, index) => {
                // Skip if restaurant is invalid
                if (!restaurant || !restaurant.restaurantId) {
                  return null;
                }

                return (
                  <DropdownItem
                    key={restaurant.restaurantId}
                    onClick={() =>
                      handleRestaurantChange(restaurant.restaurantId)
                    }
                    className="whitespace-nowrap"
                  >
                    {restaurant.name || `Restaurant ${index + 1}`}
                  </DropdownItem>
                );
              })}
            </Dropdown>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center gap-4">
          {selectedRestaurant && (
            <RestaurantStatus
              capacity={selectedRestaurant.capacity}
              description={selectedRestaurant.description}
              schedule={restaurantStatusDisplayProps}
            />
          )}
          {isSaving && (
            <span className="text-sm text-gray-500">Saving changes...</span>
          )}
        </div>
      </div>

      <div className="mt-8">
        <h2 className="text-lg font-semibold mb-2">Description</h2>
        {isEditingDescription ? (
          <div>
            <textarea
              className="w-full p-2 border rounded mb-2 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-white"
              value={tempDescription}
              onChange={(e) => setTempDescription(e.target.value)}
              rows={3}
            />
            <div>
              <button
                onClick={handleDescriptionSave}
                disabled={isSaving}
                className="mr-2 bg-blue-500 text-white px-3 py-1 rounded disabled:opacity-50"
              >
                Save
              </button>
              <button
                onClick={handleDescriptionCancel}
                disabled={isSaving}
                className="bg-gray-300 dark:bg-gray-700 text-black dark:text-white px-3 py-1 rounded disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <p className="text-gray-800 dark:text-gray-300">
              {selectedRestaurant?.description || "No description available."}
            </p>
            <button
              onClick={() => setIsEditingDescription(true)}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Edit
            </button>
          </div>
        )}
      </div>

      {/* Header Photo Upload */}
      {selectedRestaurant && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold mb-4">
            Restaurant Header Photo
          </h2>

          <div className="space-y-4 max-w-md">
            {/* Image preview/placeholder handled within ImageUploader */}
            <ImageUploader
              entityType="restaurant-header"
              entityId={selectedRestaurant.restaurantId}
              currentImageUrl={selectedRestaurant.headerPhoto || undefined}
              onUploadComplete={handleHeaderPhotoUpload}
              onUploadError={(err) => setUpdateError(err)}
              onDeleteImage={handleHeaderPhotoDelete}
              disabled={isSaving}
            />
          </div>
        </div>
      )}

      <hr className="my-8 border-gray-200 dark:border-gray-700" />

      <div className="w-full">
        <div className="md:w-full mb-8 md:mb-0">
          {/* Show hookError and operatingHoursError as a non-intrusive alert above operating hours */}
          {(hookError || operatingHoursError) && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800 dark:text-red-300">
                    {operatingHoursError || 
                     (hookError?.message || "An error occurred while loading operating hours.")}
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold">Operating Hours</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Restaurant operating hours.
              </p>
            </div>
            {isEditingOperatingHours ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleOperatingHoursSave}
                  disabled={isSaving || !!updateError}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <div className="flex items-center gap-2">
                      <LoadingSpinner size="sm" color="primary" />
                      <span>Saving...</span>
                    </div>
                  ) : (
                    "Save"
                  )}
                </button>
                  <button
                  onClick={() => {
                    setOperatingHoursError(null);
                    setIsEditingOperatingHours(false);
                  }}
                  disabled={isSaving}
                  className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsEditingOperatingHours(true)}
                disabled={!!hookError}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Edit
              </button>
            )}
          </div>

          <div className="mt-4 space-y-2">
            {allDays.map((day: DayOfWeek) => {
              // Use the transformed map for display
              const currentDayApiSchedule = currentOperatingSchedulesMap[day];
              const tempDayHours = tempOperatingHours[day];

              return (
                <div
                  key={day}
                  className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-2"
                >
                  <span className="w-24 text-gray-800 dark:text-gray-300">
                    {day}
                  </span>
                  {isEditingOperatingHours ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="time"
                        value={tempDayHours?.open || ""}
                        onChange={(e) =>
                          handleOperatingHoursChange(
                            day,
                            "open",
                            e.target.value
                          )
                        }
                        className="w-32"
                      />
                      <span className="mx-1 text-gray-800 dark:text-gray-300">
                        -
                      </span>
                      <Input
                        type="time"
                        value={tempDayHours?.close || ""}
                        onChange={(e) =>
                          handleOperatingHoursChange(
                            day,
                            "close",
                            e.target.value
                          )
                        }
                        className="w-32"
                      />
                    </div>
                  ) : (
                    <div className="text-right">
                      {currentDayApiSchedule &&
                      currentDayApiSchedule.openTime &&
                      currentDayApiSchedule.closeTime ? (
                        <span className="text-gray-800 dark:text-gray-300">
                          {formatTimeForDisplay(currentDayApiSchedule.openTime)}{" "}
                          -{" "}
                          {formatTimeForDisplay(
                            currentDayApiSchedule.closeTime
                          )}
                        </span>
                      ) : (
                        <span className="text-gray-500 dark:text-gray-400">
                          No Data Available
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantsData;
